import { useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';

interface CreatedAsset {
  readonly assetId: string;
  readonly assetCode: string;
  readonly assetName: string;
  readonly lifecycleStatus: string;
}

const ASSET_TYPES = ['IMAGE', 'VIDEO', 'AUDIO', 'DOCUMENT', 'VECTOR', 'FONT', 'TEMPLATE', 'ARCHIVE', 'PRESENTATION', 'SPREADSHEET', 'OTHER'];
const CATEGORIES = ['CORPORATE_BRANDING', 'PRODUCT_BRANDING', 'CAMPAIGN_ASSETS', 'PRINT_MEDIA', 'OUTDOOR_MEDIA', 'DIGITAL_MEDIA', 'SOCIAL_MEDIA_CREATIVES', 'VIDEO_ASSETS', 'PHOTOGRAPHY_ASSETS', 'VENDOR_DELIVERABLES', 'TEMPLATES', 'REFERENCE_ASSETS'];
const CLASSIFICATIONS = ['PUBLIC', 'INTERNAL', 'CONFIDENTIAL', 'RESTRICTED'];

/**
 * M36 — Asset Upload Screen (§31). Ingest workbench that registers a new
 * digital asset (T480) with its descriptive metadata (name, category,
 * asset type, security classification, description) and the source file URI,
 * then posts it to the repository where it begins the DRAFT lifecycle. The
 * mandatory fields (asset name + category) are validated client-side before
 * submit; the server enforces the authoritative rules and tenant isolation
 * (LAW 4). A 403 surfaces a clear permission message.
 */
export function AssetUploadScreen(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [assetName, setAssetName] = useState('');
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [assetType, setAssetType] = useState(ASSET_TYPES[0]);
  const [classification, setClassification] = useState(CLASSIFICATIONS[1]);
  const [description, setDescription] = useState('');
  const [fileUri, setFileUri] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedAsset | null>(null);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setCreated(null);
    if (!assetName.trim()) {
      setActionError(t('m36.upload.nameReq'));
      return;
    }
    if (!category) {
      setActionError(t('m36.upload.categoryReq'));
      return;
    }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: CreatedAsset }>(`/v1/dam/assets`, {
        method: 'POST',
        body: {
          assetName: assetName.trim(),
          category,
          assetType,
          classification,
          description: description.trim() || undefined,
          fileUri: fileUri.trim() || undefined,
        },
        token,
      });
      setCreated(r.data);
      setAssetName('');
      setDescription('');
      setFileUri('');
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m36.upload.notAuthorised'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <main aria-label="asset-upload">
      <h1>{t('m36.upload.title')}</h1>
      <form aria-label="upload-form" onSubmit={(e) => void submit(e)}>
        <label>
          {t('m36.upload.nameLabel')}
          <input aria-label="field-name" value={assetName} onChange={(e) => setAssetName(e.target.value)} />
        </label>
        <label>
          {t('m36.common.category')}
          <select aria-label="field-category" value={category} onChange={(e) => setCategory(e.target.value)}>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label>
          {t('m36.upload.typeLabel')}
          <select aria-label="field-type" value={assetType} onChange={(e) => setAssetType(e.target.value)}>
            {ASSET_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label>
          {t('m36.common.classification')}
          <select aria-label="field-classification" value={classification} onChange={(e) => setClassification(e.target.value)}>
            {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label>
          {t('m36.upload.descriptionLabel')}
          <textarea aria-label="field-description" value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
        <label>
          {t('m36.upload.fileUriLabel')}
          <input aria-label="field-fileuri" value={fileUri} onChange={(e) => setFileUri(e.target.value)} placeholder="s3://…" />
        </label>
        <Button variant="primary" type="submit" aria-label="submit-upload" disabled={busy}>
          {busy ? t('m36.upload.uploading') : t('m36.upload.submit')}
        </Button>
      </form>

      {actionError && <p role="alert" aria-label="upload-error">{actionError}</p>}
      {created && (
        <dl role="status" aria-label="upload-result">
          <dt>{t('m36.upload.codeLabel')}</dt><dd aria-label="result-code">{created.assetCode}</dd>
          <dt>{t('m36.upload.nameLabel')}</dt><dd aria-label="result-name">{created.assetName}</dd>
          <dt>{t('m36.common.status')}</dt><dd aria-label="result-status">{created.lifecycleStatus}</dd>
        </dl>
      )}
    </main>
  );
}
